import type { SurfaceTone } from '@workspace/ui/lib/tone'
import type { HomeStateKey } from './copy.js'

/**
 * The three phases of the journey, in order. The rail shows where the founder
 * is; the focus card shows what to do about it.
 */
export const RAIL_PHASES = [
  { id: 'feedback', label: 'Feedback' },
  { id: 'profile', label: 'Profile & Proof' },
  { id: 'share', label: 'Share' },
  { id: 'outreach', label: 'Outreach' },
] as const

export type RailPhaseId = (typeof RAIL_PHASES)[number]['id']

interface RailPosition {
  phase: RailPhaseId
  /** The "<Section> · <what's happening>" line under the rail. */
  detail: string
}

/**
 * Where each state sits, and what the substep line says.
 *
 * Typed against the generated key union, so a state the resolver can return but
 * that has no place on the rail fails the build rather than rendering a wizard
 * with no position.
 */
export const RAIL_POSITION = {
  QUEST_UPLOAD_DECK: { phase: 'feedback', detail: 'get your read' },
  QUEST_READING_DECK: { phase: 'feedback', detail: 'reading your deck' },
  QUEST_READ_FAILED: { phase: 'feedback', detail: 'the read failed' },
  QUEST_COMPLETE_PROFILE: { phase: 'profile', detail: 'complete your profile' },
  QUEST_SHARE: { phase: 'share', detail: 'your link is ready' },
  QUEST_FINDING_INVESTORS: { phase: 'outreach', detail: 'finding your investors' },
  QUEST_REVIEW_MESSAGES: { phase: 'outreach', detail: 'choose and review' },
  QUEST_APPROVE_OUTREACH: { phase: 'outreach', detail: 'approve contacts' },
  QUEST_CONNECT_LINKEDIN: { phase: 'outreach', detail: 'connect and go live' },
  QUEST_PREPARING_LINK: { phase: 'outreach', detail: 'preparing your link' },
  QUEST_LAUNCH_CAMPAIGN: { phase: 'outreach', detail: 'ready to launch' },
  QUEST_LIVE: { phase: 'outreach', detail: 'live' },
  QUEST_PAUSED: { phase: 'outreach', detail: 'paused by you' },
  QUEST_RECONNECT: { phase: 'outreach', detail: 'ready to reconnect' },
  MOMENT_REPLY: { phase: 'outreach', detail: 'replies are in' },
  MOMENT_OUTCOME_VERIFYING: { phase: 'outreach', detail: 'being verified' },
  MOMENT_OUTCOME_CONFIRMED: { phase: 'outreach', detail: 'call secured' },
  ALERT_RECONNECT: { phase: 'outreach', detail: 'disconnected' },
  ALERT_SYSTEM_STALL: { phase: 'outreach', detail: 'stopped on our side' },
  ALERT_ADD_FUNDS: { phase: 'outreach', detail: 'stopped sending' },
  ALERT_QUEUE_DRAINED: { phase: 'outreach', detail: 'queue drained' },
} as const satisfies Record<HomeStateKey, RailPosition>

/**
 * Red treatment: red eyebrow, red headline accent, red card border, red rail
 * node. Something is broken and it is not the founder's doing.
 *
 * Deliberately narrow. Red is the only colour left to say "we broke", so it is
 * never spent on an ordinary ask — see WARN_KEYS.
 *
 * A lookup set rather than a `key.startsWith('ALERT_')` test, because the
 * prefix gets it wrong in both directions. QUEST_READ_FAILED *is* a fault, and
 * QUEST_RECONNECT is not: the founder disconnected on purpose, and alarming
 * them about their own choice is how a wizard loses trust.
 */
const ALERT_KEYS: ReadonlySet<HomeStateKey> = new Set<HomeStateKey>([
  'QUEST_READ_FAILED',
  'ALERT_RECONNECT',
  'ALERT_SYSTEM_STALL',
])

/**
 * Amber treatment: the founder has to do something before the product can
 * carry on — top up the wallet, approve more contacts. Nothing is broken, and
 * dressing an ordinary ask as a failure trains people to ignore the colour
 * that means a real one.
 */
const WARN_KEYS: ReadonlySet<HomeStateKey> = new Set<HomeStateKey>([
  'ALERT_ADD_FUNDS',
  'ALERT_QUEUE_DRAINED',
])

/**
 * How a state is dressed.
 *
 * Distinct from the payload's own `isAlert`, which answers a different
 * question — "does this startup need someone to look at it" — and is what the
 * admin table flags on. Add-funds and drained are attention there, and amber
 * here; the two are not the same axis.
 */
export const toneForState = (key: HomeStateKey): SurfaceTone =>
  ALERT_KEYS.has(key) ? 'alert' : WARN_KEYS.has(key) ? 'warn' : 'default'

/** Index of the phase a state sits in, for done/current/pending on the rail. */
export const phaseIndex = (key: HomeStateKey): number =>
  RAIL_PHASES.findIndex((phase) => phase.id === RAIL_POSITION[key].phase)
